
import React from 'react';
import { ArrowLeft, Play, BookOpen, Edit3, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { Folder } from '../data/types';
import { latexToPlainText } from '../utils';

interface LectureViewProps {
    folder: Folder;
    onBack: () => void;
    onStartQuiz: () => void;
    onEdit?: () => void;
}

// --- Enhanced Markdown Parser Logic ---

// 1. Inline Parser: Handles Bold, Italic, Code, Math, Links, Images
const parseInline = (text: string): React.ReactNode[] => {
    if (!text) return [];

    // Order matters: Image -> Link -> Code -> Math -> Bold -> Italic
    // Regex explanation:
    // Image: !\[(.*?)\]\((.*?)\)
    // Link: \[((?:\[[^\]]*\]|[^\[\]])*)\]\((.*?)\)  <- Slightly complex to handle nested brackets if needed, but simple one is \[([^\]]+)\]\(([^)]+)\)
    // Code: `([^`]+)`
    // Math: \$([^$]+)\$
    // Bold: \*\*([^*]+)\*\*
    // Italic: \*([^*]+)\*

    // We split by a combined regex to tokenise the string
    const regex = /(!\[.*?\]\(.*?\))|(\[.*?\]\(.*?\))|(`[^`]+`)|(\$[^$]+\$)|(\*\*[^*]+\*\*)|(\*[^*]+\*)/g;
    
    const parts = text.split(regex).filter(p => p !== undefined && p !== "");

    return parts.map((part, index) => {
        // Image
        if (part.startsWith('![') && part.endsWith(')')) {
            const match = part.match(/!\[(.*?)\]\((.*?)\)/);
            if (match) {
                return (
                    <div key={index} className="my-4 flex flex-col items-center">
                        <img src={match[2]} alt={match[1]} className="max-w-full h-auto rounded-lg shadow-sm border border-gray-200" loading="lazy" />
                        {match[1] && <span className="text-sm text-gray-500 mt-2 italic">{match[1]}</span>}
                    </div>
                );
            }
        }
        // Link
        if (part.startsWith('[') && part.endsWith(')')) {
            const match = part.match(/\[(.*?)\]\((.*?)\)/);
            if (match) {
                return (
                    <a key={index} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center">
                        {latexToPlainText(match[1])} <ExternalLink className="w-3 h-3 ml-0.5 opacity-70" />
                    </a>
                );
            }
        }
        // Inline Code
        if (part.startsWith('`') && part.endsWith('`')) {
            return <code key={index} className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded font-mono text-sm border border-gray-200 mx-0.5">{part.slice(1, -1)}</code>;
        }
        // Math (Simple Inline LaTeX style)
        if (part.startsWith('$') && part.endsWith('$')) {
            return <span key={index} className="font-serif italic text-fudan-blue px-1">{latexToPlainText(part.slice(1, -1))}</span>;
        }
        // Bold
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index} className="font-bold text-gray-900 bg-yellow-50 px-0.5">{latexToPlainText(part.slice(2, -2))}</strong>;
        }
        // Italic
        if (part.startsWith('*') && part.endsWith('*')) {
            return <em key={index} className="italic text-gray-800">{latexToPlainText(part.slice(1, -1))}</em>;
        }

        // Plain Text (Apply LaTeX conversion here too for inputs like "12 \div 3")
        return <span key={index}>{latexToPlainText(part)}</span>;
    });
};

// 2. Block Parser: Handles Structure (Headers, Lists, Tables, Code Blocks, Quotes)
const MarkdownRenderer = ({ content }: { content: string }) => {
    if (!content) return <p className="text-gray-400 italic text-center py-10">此單元尚無講義內容。</p>;

    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    
    let inCodeBlock = false;
    let codeBuffer: string[] = [];
    let inTable = false;
    let tableBuffer: string[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // --- 1. Code Blocks ---
        if (trimmed.startsWith('```')) {
            if (inCodeBlock) {
                // End of code block
                elements.push(
                    <div key={`code-${i}`} className="bg-gray-800 text-gray-100 p-4 rounded-xl my-4 font-mono text-sm overflow-x-auto shadow-inner">
                        <pre>{codeBuffer.join('\n')}</pre>
                    </div>
                );
                codeBuffer = [];
                inCodeBlock = false;
            } else {
                // Start of code block
                inCodeBlock = true;
            }
            continue;
        }
        if (inCodeBlock) {
            codeBuffer.push(line);
            continue;
        }

        // --- 2. Tables ---
        // Simple detection: line starts and ends with |
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            inTable = true;
            tableBuffer.push(trimmed);
            // Check if next line is NOT a table row, then render
            const nextLine = lines[i + 1]?.trim();
            if (!nextLine || !nextLine.startsWith('|')) {
                // Render Table
                const headerRow = tableBuffer[0];
                const separatorRow = tableBuffer[1]; // Usually |---|---|
                const bodyRows = tableBuffer.slice(separatorRow && separatorRow.includes('-') ? 2 : 1);

                const parseRow = (rowStr: string) => rowStr.split('|').filter(c => c).map(c => c.trim());

                elements.push(
                    <div key={`table-${i}`} className="overflow-x-auto my-6 rounded-lg border border-gray-200 shadow-sm">
                        <table className="min-w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-700 font-bold uppercase">
                                <tr>
                                    {parseRow(headerRow).map((cell, idx) => (
                                        <th key={idx} className="px-6 py-3 border-b border-gray-200">{parseInline(cell)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {bodyRows.map((row, rIdx) => (
                                    <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                        {parseRow(row).map((cell, cIdx) => (
                                            <td key={cIdx} className="px-6 py-3 text-gray-700">{parseInline(cell)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
                tableBuffer = [];
                inTable = false;
            }
            continue;
        }

        // --- 3. Headers ---
        if (trimmed.startsWith('# ')) {
            elements.push(<h1 key={i} className="text-3xl font-serif font-bold text-fudan-blue mt-8 mb-4 pb-2 border-b-2 border-blue-100">{parseInline(trimmed.substring(2))}</h1>);
            continue;
        }
        if (trimmed.startsWith('## ')) {
            elements.push(<h2 key={i} className="text-2xl font-serif font-bold text-gray-800 mt-6 mb-3 flex items-center"><span className="w-2 h-8 bg-yellow-400 mr-3 rounded-full"></span>{parseInline(trimmed.substring(3))}</h2>);
            continue;
        }
        if (trimmed.startsWith('### ')) {
            elements.push(<h3 key={i} className="text-xl font-bold text-blue-700 mt-5 mb-2">{parseInline(trimmed.substring(4))}</h3>);
            continue;
        }
        if (trimmed.startsWith('#### ')) {
            elements.push(<h4 key={i} className="text-lg font-bold text-gray-700 mt-4 mb-2 border-l-4 border-gray-300 pl-2">{parseInline(trimmed.substring(5))}</h4>);
            continue;
        }

        // --- 4. Blockquotes ---
        if (trimmed.startsWith('> ')) {
            elements.push(
                <blockquote key={i} className="border-l-4 border-fudan-blue bg-blue-50/50 pl-4 py-3 my-4 italic text-gray-700 rounded-r-lg">
                    {parseInline(trimmed.substring(2))}
                </blockquote>
            );
            continue;
        }

        // --- 5. Horizontal Rule ---
        if (trimmed === '---' || trimmed === '***') {
            elements.push(<hr key={i} className="my-8 border-gray-200" />);
            continue;
        }

        // --- 6. Lists ---
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            elements.push(
                <li key={i} className="ml-6 list-disc text-gray-700 mb-1 pl-1 marker:text-fudan-blue">
                    {parseInline(trimmed.substring(2))}
                </li>
            );
            continue;
        }
        // Ordered List (Simple detection for 1. 2. etc)
        const orderedMatch = trimmed.match(/^(\d+)\.\s(.*)/);
        if (orderedMatch) {
             elements.push(
                <div key={i} className="flex ml-2 mb-2 text-gray-700">
                    <span className="font-bold text-fudan-blue w-6 shrink-0 text-right mr-3">{orderedMatch[1]}.</span>
                    <div>{parseInline(orderedMatch[2])}</div>
                </div>
            );
            continue;
        }

        // --- 7. Empty Lines ---
        if (trimmed === '') {
            elements.push(<div key={i} className="h-4"></div>);
            continue;
        }

        // --- 8. Standard Paragraph ---
        elements.push(<p key={i} className="text-gray-700 mb-2 leading-8 text-lg">{parseInline(line)}</p>);
    }

    return <div className="space-y-1">{elements}</div>;
};

export const LectureView = ({ folder, onBack, onStartQuiz, onEdit }: LectureViewProps) => {
    return (
        <div className="flex flex-col h-full bg-white relative">
            {/* Header */}
            <div className="bg-white/95 backdrop-blur-sm border-b border-gray-200 px-4 py-3 flex justify-between items-center z-10 shadow-sm print:hidden shrink-0">
                <button 
                    onClick={onBack}
                    className="flex items-center text-gray-600 hover:text-gray-900 font-bold transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-100"
                >
                    <ArrowLeft className="w-5 h-5 mr-1" /> 返回地圖
                </button>
                <h1 className="text-lg font-serif font-bold text-fudan-blue flex items-center truncate max-w-[60%]">
                    <BookOpen className="w-5 h-5 mr-2 shrink-0" />
                    <span className="truncate">{folder.name} - 重點講義</span>
                </h1>
                <div className="w-24 flex justify-end">
                    {/* Placeholder for future actions like Print */}
                </div> 
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto bg-white">
                <div className="p-6 md:p-12 max-w-4xl mx-auto w-full min-h-[60vh]">
                    <article className="prose prose-lg prose-blue max-w-none">
                        {/* Folder Title as Document Title */}
                        <div className="text-center mb-10 pb-6 border-b border-gray-100">
                            <span className="bg-blue-50 text-fudan-blue px-3 py-1 rounded-full text-sm font-bold mb-3 inline-block tracking-wide">LECTURE NOTES</span>
                            <h1 className="text-4xl font-serif font-bold text-gray-900">{folder.name}</h1>
                        </div>

                        {/* Markdown Content */}
                        <MarkdownRenderer content={folder.noteContent || ''} />
                    </article>

                    {(!folder.noteContent) && (
                        <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 mt-8">
                            <div className="bg-white p-4 rounded-full mb-4 shadow-sm">
                                <Edit3 className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 mb-2 font-bold text-lg">老師還沒準備講義喔！</p>
                            <p className="text-sm text-gray-400">請至「題庫資源管理」編輯或使用 AI 自動生成。</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Action - Fixed at Bottom */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-center z-10 print:hidden shrink-0">
                <button 
                    onClick={onStartQuiz}
                    className="flex items-center px-8 py-3 bg-fudan-blue hover:bg-blue-800 text-white text-lg font-bold rounded-full shadow-lg transition-transform transform hover:scale-105 active:scale-95"
                >
                    <Play className="w-5 h-5 mr-2 fill-current" />
                    讀完了，開始測驗！
                </button>
            </div>
        </div>
    );
};
